(() => {
  if (customElements.get('pyp-photo-upload')) return;
  const extensions = new Set(['jpg', 'jpeg', 'png', 'webp', 'heic', 'heif']);
  const mimeTypes = new Set(['image/jpeg', 'image/pjpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif']);
  class PypPhotoUpload extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.input = this.querySelector('[data-pyp-photo-input]');
      this.form = this.input?.form;
      if (!this.form) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.maxBytes = Number(this.dataset.maxBytes) || 20971520;
      this.cropper = this.querySelector('pyp-photo-crop');
      this.preview = this.querySelector('[data-pyp-photo-preview]');
      this.thumbnail = this.querySelector('[data-pyp-photo-thumbnail]');
      this.errorMessage = this.querySelector('[data-pyp-photo-error]');
      this.status = this.querySelector('[data-pyp-photo-status]');
      this.input.addEventListener('change', () => this.syncFromInput(), { signal });
      this.input.addEventListener('invalid', event => {
        event.preventDefault(); this.showError(this.requirement()); this.focusUpload();
      }, { signal });
      this.querySelector('[data-pyp-photo-remove]').addEventListener('click', () => this.clearSelection(true), { signal });
      this.querySelector('[data-pyp-photo-edit]').addEventListener('click', event => this.edit(event.detail === 0), { signal });
      this.form.addEventListener('pyp:variant-change', () => this.onSizeChange(), { signal });
      this.form.addEventListener('pyp:orientation-change', () => this.onSizeChange(), { signal });
      this.form.addEventListener('reset', () => this.clearSelection(false), { signal });
      this.addEventListener('pyp:crop-confirmed', event => {
        const result = event.detail;
        if (result.original !== this.original || !this.sameSize(result.size, this.selectedSize())) return;
        this.ready = result;
        this.clearError(); this.preview.hidden = false; this.renderThumbnail(result.file);
        this.querySelector('[data-pyp-photo-filename]').textContent = this.original.name;
        this.querySelector('[data-pyp-photo-filesize]').textContent = result.size.width + ' × ' + result.size.height + ' cm';
        this.setStatus('');
      }, { signal });
      this.form.addEventListener('submit', event => {
        if (this.ready && this.sameSize(this.ready.size, this.selectedSize())) {
          if (this.querySelector('pyp-photo-commerce')?.hasAttribute('data-ready')) return;
          event.preventDefault(); event.stopImmediatePropagation();
          this.showError('Photo upload is still loading. Please refresh this page if it does not finish.', false);
          return;
        }
        event.preventDefault(); event.stopImmediatePropagation();
        this.showError(this.requirement()); this.focusUpload();
      }, { capture: true, signal });
      const dropzone = this.querySelector('[data-pyp-photo-dropzone]');
      ['dragenter', 'dragover'].forEach(type => dropzone.addEventListener(type, event => {
        event.preventDefault(); dropzone.classList.add('is-dragging');
      }, { signal }));
      ['dragleave', 'dragend', 'drop'].forEach(type => dropzone.addEventListener(type, () => dropzone.classList.remove('is-dragging'), { signal }));
      dropzone.addEventListener('drop', event => {
        event.preventDefault();
        if (this.form.dataset.cartSubmitPending === 'true') return;
        if (event.dataTransfer?.files.length !== 1) { this.showError('Please choose one photo for each painting.', !this.ready); return; }
        try { const transfer = new DataTransfer(); transfer.items.add(event.dataTransfer.files[0]); this.input.files = transfer.files; this.syncFromInput(); }
        catch { this.showError('Please use Upload Your Photo to choose this file.', !this.ready); }
      }, { signal });
      this.bindApprovalHelp(signal);
      this.syncFromInput();
    }

    extensionFor(file) { return String(file?.name || '').toLowerCase().split('.').pop(); }
    validate(file) {
      if (!(file instanceof File)) return 'Please choose a photo file.';
      if (!file.size) return 'The selected photo is empty. Please choose another file.';
      if (file.size > this.maxBytes) return 'The selected photo is larger than 20 MB. Please choose a smaller file.';
      if (!extensions.has(this.extensionFor(file)) || (file.type && !mimeTypes.has(file.type.toLowerCase()))) return 'Use a JPG, PNG, WebP, HEIC, or HEIF photo.';
      return '';
    }
    selectedSize() {
      const variant = this.form.querySelector('[data-pyp-variant-select]')?.selectedOptions[0]?.textContent || '';
      const match = variant.match(/\(([\d.]+)\s*x\s*([\d.]+)\s*cm\)/i);
      if (!match) return null;
      const orientation = this.form.querySelector('[data-pyp-orientation-value]')?.value === 'Horizontal' ? 'horizontal' : 'vertical';
      const short = Math.min(Number(match[1]), Number(match[2]));
      const long = Math.max(Number(match[1]), Number(match[2]));
      return { width: orientation === 'horizontal' ? long : short, height: orientation === 'horizontal' ? short : long, orientation };
    }
    sameSize(a, b) { return Boolean(a && b && a.width === b.width && a.height === b.height && (a.orientation || 'vertical') === (b.orientation || 'vertical')); }
    requirement() { return this.original ? 'Please adjust and confirm your photo for the selected canvas size.' : 'Please upload your photo before adding this product to the cart.'; }
    setStatus(message) { this.status.textContent = message; this.status.hidden = !message; }
    showError(message, blocking = true) {
      this.input.setCustomValidity(blocking ? message : '');
      const target = this.original ? this.querySelector('[data-pyp-photo-feedback]') : this;
      if (this.errorMessage.parentElement !== target) target.append(this.errorMessage);
      this.errorMessage.textContent = message; this.errorMessage.hidden = false;
      this.setStatus('');
    }
    clearError() { this.input.setCustomValidity(''); this.errorMessage.hidden = true; this.errorMessage.textContent = ''; }
    focusUpload() {
      const target = this.original ? this.querySelector('[data-pyp-photo-edit]') : this.input;
      target.focus({ preventScroll: true }); this.scrollIntoView({ block: 'center', behavior: 'auto' });
    }
    clearPreview() { if (this.objectUrl) URL.revokeObjectURL(this.objectUrl); this.objectUrl = null; this.thumbnail.removeAttribute('src'); this.thumbnail.hidden = true; }
    renderThumbnail(file) { this.clearPreview(); this.objectUrl = URL.createObjectURL(file); this.thumbnail.src = this.objectUrl; this.thumbnail.hidden = false; this.querySelector('[data-pyp-photo-placeholder]').hidden = true; }
    clearSelection(focus) {
      this.selectionMotion?.cancel();
      this.querySelector('[data-pyp-photo-edit]')?.removeAttribute('aria-busy');
      this.cropper.cancelPending(); this.generation = (this.generation || 0) + 1;
      this.selectionRevision = (this.selectionRevision || 0) + 1;
      this.input.value = ''; this.original = null; this.ready = null; this.preview.hidden = true;
      this.clearPreview(); this.clearError(); this.setStatus('');
      this.querySelector('[data-pyp-photo-button-label]').textContent = 'Upload Your Photo';
      this.dispatchEvent(new CustomEvent('pyp:photo-reset', { bubbles: true }));
      if (focus) this.input.focus({ preventScroll: true });
    }
    animateSelection(previousHeight) {
      this.selectionMotion?.cancel();
      const style = getComputedStyle(this);
      const inset = style.boxSizing === 'border-box' ? 0 :
        (parseFloat(style.paddingTop) || 0) + (parseFloat(style.paddingBottom) || 0) +
        (parseFloat(style.borderTopWidth) || 0) + (parseFloat(style.borderBottomWidth) || 0);
      const nextHeight = this.getBoundingClientRect().height;
      if (matchMedia('(prefers-reduced-motion: reduce)').matches || !this.animate || Math.abs(nextHeight - previousHeight) < 1) return Promise.resolve();
      const motion = this.animate([{ height: `${Math.max(0, previousHeight - inset)}px` }, { height: `${Math.max(0, nextHeight - inset)}px` }],
        { duration: 220, easing: 'cubic-bezier(0.2, 0, 0, 1)' });
      this.selectionMotion = motion;
      this.preview.animate([{ opacity: 0, transform: 'translateY(-6px)' }, { opacity: 1, transform: 'translateY(0)' }],
        { duration: 220, easing: 'ease-out' });
      return motion.finished.catch(() => {}).then(() => { if (this.selectionMotion === motion) this.selectionMotion = null; });
    }
    async syncFromInput() {
      const previousHeight = this.getBoundingClientRect().height;
      const file = this.input.files?.[0];
      if (!file) { this.clearSelection(false); return; }
      this.cropper.cancelPending(); this.ready = null;
      const error = this.validate(file);
      if (error) { this.clearSelection(false); this.showError(error); return; }
      const generation = this.generation = (this.generation || 0) + 1;
      const selectionRevision = this.selectionRevision = (this.selectionRevision || 0) + 1;
      this.original = file;
      this.clearError(); this.input.setCustomValidity(this.requirement());
      this.dispatchEvent(new CustomEvent('pyp:photo-reset', { bubbles: true }));
      const keyboard = this.input.matches(':focus-visible');
      try {
        // The thumbnail also decodes the original; check dimensions before its URL exists.
        await this.cropper.inspectPhoto(file);
        if (!this.isConnected || selectionRevision !== this.selectionRevision || this.original !== file) return;
        this.clearPreview(); this.preview.hidden = false;
        this.querySelector('[data-pyp-photo-placeholder]').hidden = false;
        this.querySelector('[data-pyp-photo-filename]').textContent = file.name;
        this.querySelector('[data-pyp-photo-filesize]').textContent = '';
        this.querySelector('[data-pyp-photo-button-label]').textContent = 'Choose another photo';
        this.renderThumbnail(file);
        this.setStatus('Choose Adjust to check and confirm the crop.');
        await this.animateSelection(previousHeight);
        if (this.isConnected && generation === this.generation && this.original === file) this.edit(keyboard);
      } catch {
        if (selectionRevision !== this.selectionRevision || this.original !== file) return;
        this.clearSelection(false);
        this.showError('We could not open this photo. Please choose a JPG, PNG, or WebP. HEIC photos work only in browsers that can open them.');
      }
    }
    async edit(keyboard = false) {
      if (!this.original || this.form.dataset.cartSubmitPending === 'true') return;
      const size = this.selectedSize();
      if (!size) { this.showError('Please select a canvas size first.'); return; }
      const generation = this.generation = (this.generation || 0) + 1;
      const editButton = this.querySelector('[data-pyp-photo-edit]');
      editButton.setAttribute('aria-busy', 'true');
      try {
        const opened = await this.cropper.openPhoto(this.original, size, this.querySelector('[data-pyp-photo-edit]'), this.ready?.crop, keyboard);
        if (generation !== this.generation || !opened) return;
        if (!this.ready) this.input.setCustomValidity(this.requirement());
        if (!this.ready) {
          // Preserve the already rendered background while the native crop dialog opens.
          if (!this.objectUrl) this.renderThumbnail(this.original);
        } else this.setStatus('');
      } catch {
        if (generation !== this.generation) return;
        this.ready = null;
        this.showError('We could not open this photo. Please choose a JPG, PNG, or WebP. HEIC photos work only in browsers that can open them.');
        this.setStatus('');
      } finally {
        if (generation === this.generation) editButton.removeAttribute('aria-busy');
      }
    }
    onSizeChange() {
      if (!this.original) return;
      this.cropper.cancelPending(); this.generation = (this.generation || 0) + 1;
      if (this.ready && this.sameSize(this.ready.size, this.selectedSize())) return;
      this.ready = null; this.clearError(); this.input.setCustomValidity(this.requirement());
      this.querySelector('[data-pyp-photo-filesize]').textContent = '';
      this.setStatus('Your canvas changed. Choose Adjust to check the new crop.');
    }
    bindApprovalHelp(signal) {
      const help = this.querySelector('[data-pyp-approve-help]');
      if (!help) return;
      const tooltip = this.querySelector('[data-pyp-approve-tooltip]');
      const wrapper = help.closest('[data-pyp-approve]');
      let keyboardOpen = false;
      const placeTooltip = () => {
        const anchor = help.getBoundingClientRect();
        const box = tooltip.getBoundingClientRect();
        const left = Math.max(12, Math.min(window.innerWidth - box.width - 12, anchor.right + 34 - box.width));
        const below = anchor.top < box.height + 24;
        const top = below ? anchor.bottom + 12 : anchor.top - box.height - 12;
        tooltip.classList.toggle('is-below', below);
        tooltip.style.left = `${left}px`;
        tooltip.style.top = `${Math.max(12, Math.min(window.innerHeight - box.height - 12, top))}px`;
        tooltip.style.setProperty('--pyp-help-arrow', `${Math.max(12, Math.min(box.width - 12, anchor.left + anchor.width / 2 - left))}px`);
      };
      const setOpen = open => {
        if (open && (!tooltip.open || tooltip.hasAttribute('data-myart-modal-closing'))) {
          tooltip.hidden = false;
          if (window.matchMedia('(max-width: 600px)').matches) {
            if (!tooltip.open) this.approvalScroll = document.documentElement.style.overflow;
            document.documentElement.style.overflow = 'hidden';
            window.MyArtModalMotion ? window.MyArtModalMotion.open(tooltip) : tooltip.showModal();
            if (keyboardOpen) tooltip.querySelector('button').focus({ preventScroll: true });
            else tooltip.focus({ preventScroll: true });
          } else { const show = () => { if (!tooltip.open) tooltip.show(); placeTooltip(); }; if (window.MyArtModalMotion) window.MyArtModalMotion.open(tooltip, show); else show(); }
        } else if (!open && tooltip.open) { if (window.MyArtModalMotion) window.MyArtModalMotion.close(tooltip); else tooltip.close(); }
        help.setAttribute('aria-expanded', String(open));
      };
      tooltip.addEventListener('close', () => {
        if (tooltip.open) return;
        tooltip.hidden = true; help.setAttribute('aria-expanded', 'false');
        tooltip.style.removeProperty('left'); tooltip.style.removeProperty('top');
        this.unlockApprovalScroll();
        if (keyboardOpen) help.focus({ preventScroll: true });
        else if (document.activeElement === help) help.blur();
      }, { signal });
      tooltip.addEventListener('cancel', event => { event.preventDefault(); keyboardOpen = true; setOpen(false); }, { signal });
      const desktopHover = () => window.matchMedia('(min-width: 601px) and (hover: hover)').matches;
      help.addEventListener('click', event => { keyboardOpen = event.detail === 0; setOpen(event.detail > 0 && desktopHover() ? true : !tooltip.open); }, { signal });
      help.addEventListener('pointerenter', event => { if (event.pointerType === 'mouse' && desktopHover()) { clearTimeout(this.helpTimer); setOpen(true); } }, { signal });
      wrapper.addEventListener('pointerleave', event => { if (event.pointerType === 'mouse' && desktopHover()) this.helpTimer = setTimeout(() => setOpen(false), 180); }, { signal });
      tooltip.addEventListener('pointerenter', () => clearTimeout(this.helpTimer), { signal });
      this.querySelector('[data-pyp-approve-close]').addEventListener('click', event => { keyboardOpen = event.detail === 0; setOpen(false); }, { signal });
      wrapper.addEventListener('keydown', event => { if (event.key === 'Escape') { keyboardOpen = true; setOpen(false); } }, { signal });
      document.addEventListener('click', event => { if (!wrapper.contains(event.target)) setOpen(false); }, { signal });
      wrapper.addEventListener('focusout', event => { if (!wrapper.contains(event.relatedTarget)) setOpen(false); }, { signal });
      window.addEventListener('resize', () => setOpen(false), { signal });
      document.addEventListener('scroll', event => { if (desktopHover() && !tooltip.contains(event.target)) setOpen(false); }, { capture: true, signal });
    }
    disconnectedCallback() {
      const tooltip = this.querySelector('[data-pyp-approve-tooltip]');
      if (tooltip) window.MyArtModalMotion?.cancel(tooltip);
      if (tooltip?.open) tooltip.close();
      if (tooltip) tooltip.hidden = true;
      this.unlockApprovalScroll();
      this.selectionMotion?.cancel();
      this.generation = (this.generation || 0) + 1; this.controller?.abort(); this.clearPreview();
      this.selectionRevision = (this.selectionRevision || 0) + 1;
      clearTimeout(this.helpTimer); this.controller = null; this.ready = null; this.original = null;
    }
    unlockApprovalScroll() {
      if (this.approvalScroll === undefined) return;
      document.documentElement.style.overflow = this.approvalScroll;
      delete this.approvalScroll;
    }
  }
  customElements.define('pyp-photo-upload', PypPhotoUpload);
})();
